/**
 * Per-case discovery requests (Lloyd, Aug 21 / Aug 24 2026).
 *
 * Separate from the ticket-level requested / received / location fields. Those fill the DS/DR
 * boxes on the journey chart. This list is the full record of each request the firm sent.
 *
 * Labels must match DISCOVERY_REQUEST_* on the backend.
 */

export type DiscoveryRequestType =
  | 'ARTICLE_39_14'
  | 'ALPR_PRESERVATION'
  | 'SELF_EXECUTING'
  | 'OTHER';

export const DISCOVERY_REQUEST_TYPES: DiscoveryRequestType[] = [
  'ARTICLE_39_14',
  'ALPR_PRESERVATION',
  'SELF_EXECUTING',
  'OTHER',
];

export const DISCOVERY_REQUEST_TYPE_LABELS: Record<DiscoveryRequestType, string> = {
  ARTICLE_39_14: 'Article 39.14 discovery request',
  ALPR_PRESERVATION: 'ALPR / flock-data preservation demand',
  SELF_EXECUTING: 'Self-executing discovery request to the prosecutor',
  OTHER: 'Other request, see the note',
};

export type DiscoveryRequestRecipient = 'COURT' | 'PROSECUTOR' | 'RECORDS_CUSTODIAN';

export const DISCOVERY_REQUEST_RECIPIENTS: DiscoveryRequestRecipient[] = [
  'COURT',
  'PROSECUTOR',
  'RECORDS_CUSTODIAN',
];

export const DISCOVERY_REQUEST_RECIPIENT_LABELS: Record<DiscoveryRequestRecipient, string> = {
  COURT: 'The court',
  PROSECUTOR: 'The prosecutor',
  RECORDS_CUSTODIAN: 'The records custodian / arresting agency',
};

export type DiscoveryRequestStatus =
  | 'draft'
  | 'unserved'
  | 'awaiting-response'
  | 'partial'
  | 'complete';

export const DISCOVERY_REQUEST_STATUS_LABELS: Record<DiscoveryRequestStatus, string> = {
  draft: 'Draft',
  unserved: 'Not served',
  'awaiting-response': 'Awaiting response',
  partial: 'Partial',
  complete: 'Complete',
};

export interface CaseDiscoveryRequestDto {
  CaseDiscoveryRequest_guid: string;
  CaseDiscoveryRequest_Type: string;
  CaseDiscoveryRequest_Recipient: string;
  CaseDiscoveryRequest_RequestedAt: string | null;
  CaseDiscoveryRequest_ServedAt: string | null;
  CaseDiscoveryRequest_ServedMethod: string | null;
  CaseDiscoveryRequest_RespondedAt: string | null;
  CaseDiscoveryRequest_Outstanding: string | null;
  CaseDiscoveryRequest_Note: string | null;
  CaseDiscoveryRequest_RecordedBy: string | null;
  status?: DiscoveryRequestStatus;
}

export interface DiscoveryRequestsResponse {
  requests: CaseDiscoveryRequestDto[];
  outstanding: Array<{
    guid?: string;
    type: string;
    recipient: string;
    requestedAt: string | null;
    servedAt: string | null;
    servedMethod: string | null;
    respondedAt: string | null;
    outstanding: string | null;
  }>;
}

export interface CreateDiscoveryRequestInput {
  CaseDiscoveryRequest_Type: DiscoveryRequestType;
  CaseDiscoveryRequest_Recipient: DiscoveryRequestRecipient;
  CaseDiscoveryRequest_RequestedAt?: string | null;
  CaseDiscoveryRequest_Note?: string | null;
}

// Webpack injects API_HOST at build time. This file has no Node types, so declare the bit we read.
declare const process: { env: { API_HOST?: string } };

export default class DiscoveryRequestApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async list(ticketGuid: string): Promise<DiscoveryRequestsResponse> {
    const response = await fetch(
      `${DiscoveryRequestApi.host()}/api/v1/manager/ticket/${ticketGuid}/discovery-requests`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to load the discovery requests');
    }
    return response.json();
  }

  static async create(
    ticketGuid: string,
    input: CreateDiscoveryRequestInput,
  ): Promise<CaseDiscoveryRequestDto> {
    const response = await fetch(
      `${DiscoveryRequestApi.host()}/api/v1/manager/ticket/${ticketGuid}/discovery-requests`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to record the discovery request');
    }
    return response.json();
  }
}
